import React, { useState } from 'react';
import { Plus, ChevronDown, ChevronUp } from 'lucide-react';

interface BookingResourceListProps {
  resources: any[];
  onRemoveResource?: (resourceId: string) => Promise<void>;
  onAddResource?: () => void;
  selectedResourceIds?: Set<string>;
}

export default function BookingResourceList({
  resources,
  onRemoveResource,
  onAddResource,
  selectedResourceIds = new Set()
}: BookingResourceListProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  // Generate avatar for a resource
  const getResourceAvatar = (resource: any) => {
    const initials = resource.resource?.first_name && resource.resource?.last_name 
      ? `${resource.resource.first_name[0]}${resource.resource.last_name[0]}`.toUpperCase()
      : '??';
    
    if (resource.resource?.avatar_url) {
      return (
        <img
          src={resource.resource.avatar_url}
          alt={`${resource.resource?.first_name} ${resource.resource?.last_name}`}
          className="w-full h-full object-cover"
        />
      );
    }
    return <span className="text-xs font-bold text-white">{initials}</span>;
  };

  if (!isExpanded) {
    // Collapsed view - overlapping avatars
    return (
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(true)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Overlapping avatars */}
            {resources.length > 0 ? (
              <div className="flex -space-x-2">
                {resources.slice(0, 5).map((resource, index) => (
                  <div 
                    key={resource.id}
                    className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 border-2 border-white flex items-center justify-center overflow-hidden relative z-10"
                    style={{ zIndex: 5 - index }}
                  >
                    {getResourceAvatar(resource)}
                  </div>
                ))}
                {resources.length > 5 && (
                  <div className="h-8 w-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                    +{resources.length - 5}
                  </div>
                )}
              </div>
            ) : (
              <span className="text-xs text-gray-500 italic">No resources assigned</span>
            )}
          </div>
          
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </div>
      </div>
    );
  }

  // Expanded view - full list
  return (
    <div>
      <div className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
           onClick={() => setIsExpanded(false)}>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Click to collapse</span>
          <ChevronUp className="h-4 w-4 text-gray-400" />
        </div>
      </div>
      
      <div className="p-4">
        <div className="space-y-2">
          {resources.map((resource) => {
            return (
              <div key={resource.id} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center overflow-hidden">
                    {getResourceAvatar(resource)}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {resource.resource?.first_name} {resource.resource?.last_name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {resource.resource?.permission_role?.name || resource.role.charAt(0).toUpperCase() + resource.role.slice(1)}
                    </div>
                  </div>
                </div>
                {onRemoveResource && (
                  <button
                    onClick={() => onRemoveResource(resource.resource_id)}
                    className="text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}
          {resources.length === 0 && (
            <div className="py-3 text-center">
              <p className="text-xs text-gray-500 italic">No resources assigned</p>
            </div>
          )}
        </div>
        
        {/* Add Resource Button */}
        {onAddResource && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <button
              onClick={onAddResource}
              className="w-full flex items-center justify-center px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-800 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Resource
            </button>
          </div>
        )}
      </div>
    </div>
  );
}